import { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { StarRating } from './StarRating';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WriteReviewModal = ({ 
  isOpen, 
  onClose, 
  productId, 
  productName,
  onReviewSubmitted 
}) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (comment.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/products/${productId}/reviews`,
        {
          rating,
          title: title.trim() || null,
          comment: comment.trim(),
          images: []
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onReviewSubmitted?.();
        onClose();
        // Reset form
        setRating(0);
        setTitle('');
        setComment('');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="write-review-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[charcoal]">Write a Review</h2>
            <p className="text-sm text-[charcoal-light] mt-0.5">{productName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-review-modal"
          >
            <X className="w-5 h-5 text-[charcoal-light]" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Rating */}
          <div>
            <label className="block text-sm font-semibold text-[charcoal] mb-3">
              Your Rating *
            </label>
            <div className="flex items-center gap-4">
              <StarRating 
                rating={rating} 
                size="xl" 
                interactive={true}
                onRatingChange={setRating}
              />
              <span className="text-sm text-[charcoal-light]">
                {rating === 0 && 'Tap to rate'}
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </span>
            </div>
          </div>

          {/* Title (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-[charcoal] mb-2">
              Review Title <span className="font-normal text-[charcoal-light]">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience"
              maxLength={100}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[gold] focus:border-transparent text-[charcoal] placeholder:text-[charcoal-light]"
              data-testid="review-title-input"
            />
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-semibold text-[charcoal] mb-2">
              Your Review *
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this product. What did you like or dislike?"
              rows={4}
              maxLength={1000}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[gold] focus:border-transparent text-[charcoal] placeholder:text-[charcoal-light] resize-none"
              data-testid="review-comment-input"
            />
            <p className="text-xs text-[charcoal-light] mt-1 text-right">
              {comment.length}/1000 characters
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || rating === 0 || comment.trim().length < 10}
            className="w-full bg-[charcoal] text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#2A2A2A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Review
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WriteReviewModal;
